import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Clock, Users, Eye, Loader2, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../api/client';

const fmtTime = (s) => {
  s = Math.round(s || 0);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

const HEAT_COLORS = ['#7B4FD8', '#8F63E0', '#A47DE7', '#B897EE', '#CBB2F3', '#DECBF8', '#EDE2FB'];

export const StudentEngagementCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiClient.request(`/api/telemetry/admin/summary?days=${windowDays}`);
      setData(d);
    } catch { /* leave last data visible */ }
    setLoading(false);
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const heatmap = data?.heatmap || [];
  const schools = data?.schools || [];
  const maxSecs = Math.max(1, ...heatmap.map(h => h.seconds));

  return (
    <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="student-engagement-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={18} style={{ color: '#7B4FD8' }} />
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Student Engagement</h3>
          <span className="text-xs text-gray-400">time spent · focus heatmap · spare beta seats</span>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30].map(d => (
            <button key={d} onClick={() => setWindowDays(d)}
              className="px-3 py-1 rounded-lg text-xs font-semibold"
              style={windowDays === d ? { backgroundColor: '#1A1209', color: 'white' } : { backgroundColor: '#F0F0F0', color: '#666' }}
              data-testid={`engagement-window-${d}d`}>{d}d</button>
          ))}
          <button onClick={load} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="engagement-refresh-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin text-gray-400' : 'text-gray-500'} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#7B4FD8' }} /></div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Active users', value: totals.active_users || 0, icon: Users, color: '#2EAA5C' },
              { label: 'Total time', value: fmtTime(totals.total_seconds), icon: Clock, color: '#7B4FD8' },
              { label: 'Avg / user', value: fmtTime(totals.avg_seconds_per_user), icon: Clock, color: '#2563EB' },
              { label: 'Page views', value: totals.views || 0, icon: Eye, color: '#F5831F' },
            ].map((m, i) => (
              <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: '#F9F9F9' }} data-testid={`engagement-stat-${i}`}>
                <div className="flex items-center gap-1.5 text-xs" style={{ color: '#666' }}>
                  <m.icon size={12} style={{ color: m.color }} /> {m.label}
                </div>
                <p className="text-2xl font-bold mt-1" style={{ color: '#1A1209' }}>{m.value}</p>
              </div>
            ))}
          </div>

          {/* Focus heatmap */}
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#7B4FD8' }}>Focus heatmap — where students spend their time</p>
            {heatmap.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-3" data-testid="engagement-heatmap-empty">No student activity recorded yet in this window.</p>
            ) : (
              <div className="space-y-1.5" data-testid="engagement-heatmap">
                {heatmap.map((h, i) => (
                  <div key={h.area} className="flex items-center gap-2" data-testid={`heatmap-row-${h.area.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                    <span className="text-xs w-28 shrink-0 font-medium" style={{ color: '#44403A' }}>{h.area}</span>
                    <div className="flex-1 h-5 rounded bg-gray-100 overflow-hidden">
                      <div className="h-full rounded transition-all" style={{
                        width: `${Math.max(4, (h.seconds / maxSecs) * 100)}%`,
                        backgroundColor: HEAT_COLORS[Math.min(i, HEAT_COLORS.length - 1)],
                      }} />
                    </div>
                    <span className="text-xs w-20 text-right font-semibold" style={{ color: '#1A1209' }}>{fmtTime(h.seconds)}</span>
                    <span className="text-[10px] w-14 text-right text-gray-400">{h.views} views</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Per-school engagement + spare seats */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#2EAA5C' }}>Schools — engagement &amp; spare beta seats</p>
            {schools.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-3">No active schools yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="engagement-schools-table">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b" style={{ borderColor: '#E0E0E0' }}>
                      <th className="py-2 pr-3 font-medium">School</th>
                      <th className="py-2 pr-3 font-medium">Active students</th>
                      <th className="py-2 pr-3 font-medium">Time spent</th>
                      <th className="py-2 pr-3 font-medium">Seats used</th>
                      <th className="py-2 font-medium">Spare seats</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map(s => {
                      const pct = s.student_limit ? Math.round((s.students_enrolled / s.student_limit) * 100) : 0;
                      return (
                        <tr key={s.school_id} className="border-t" style={{ borderColor: '#F0F0F0' }} data-testid={`engagement-school-${s.school_id}`}>
                          <td className="py-2.5 pr-3">
                            <span className="font-semibold" style={{ color: '#1A1209' }}>{s.school_name}</span>
                            {s.licensed && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>LICENSED</span>}
                          </td>
                          <td className="py-2.5 pr-3">{s.active_students}</td>
                          <td className="py-2.5 pr-3 font-semibold">{fmtTime(s.seconds)}</td>
                          <td className="py-2.5 pr-3">
                            {s.students_enrolled} / {s.student_limit}
                            <span className="ml-1.5 text-[10px] text-gray-400">({pct}%)</span>
                          </td>
                          <td className="py-2.5">
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full"
                              style={s.spare_seats === 0
                                ? { backgroundColor: '#FEF2F2', color: '#DC2626' }
                                : pct >= 80
                                  ? { backgroundColor: '#FEF3C7', color: '#92400E' }
                                  : { backgroundColor: '#ECFDF5', color: '#059669' }}
                              data-testid={`spare-seats-${s.school_id}`}>
                              {pct >= 80 && <AlertTriangle size={10} />}
                              {s.spare_seats === 0 ? 'Full' : `${s.spare_seats} spare`}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <p className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-4 pt-3 border-t" style={{ borderColor: '#F0F0F0' }} data-testid="engagement-privacy-note">
            <ShieldCheck size={12} className="text-emerald-500 shrink-0" />
            Aggregate engagement only — no conversation content is ever recorded. When a student deletes a chat it is hard-deleted from our servers with no copies kept.
          </p>
        </>
      )}
    </div>
  );
};

export default StudentEngagementCard;
